import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { AuthGuard } from "./guards/auth.guard";
import { SiteComponent } from "./components/site/site.component";

import { NotFoundComponent } from "./pages/404/404.component";

import { HedgeBranch } from "./pages/exposure/hedge/hedge.component";
import { FundBranch } from "./pages/exposure/fund/fund.component";
import { AccExpHedgeBranch } from "./pages/exposure/acc-exp-hedge/acc-exp-hedge.component";
import { AccExpFundBranch } from "./pages/exposure/acc-exp-fund/acc-exp-fund.component";

import { TimingBranch } from "./pages/rules/timing/timing.component";
import { NettingBranch } from "./pages/rules/netting/netting.component";
import { BrokerSelectionBranch } from "./pages/rules/broker-selection/broker-selection.component";

import { OpenFXBranch } from "./pages/orders/openfx/openfx.component";
import { HedgeTradesBranch } from "./pages/orders/hedge-trades/hedge-trades.component";

import { FXOrdersHistory } from "./pages/history/fx-orders-h/fx-orders-h.component";
import { AssetTradesHistory } from "./pages/history/asset-trades-h/asset-trades-h.component";
import { SubFXOrdersHistory } from "./pages/history/sub-fx-orders-h/sub-fx-orders-h.component";
import { FXOrderRecommendationsHistory } from "./pages/history/fx-recommendations-h/fx-recommendations-h.component";
import { InternalAllocationsHistory } from "./pages/history/int-allocations-h/int-allocations-h.component";
import { TimingHistory } from "./pages/history/timing-h/timing-h.component";
import { NettingHistory } from "./pages/history/netting-h/netting-h.component";
import { BrokerSelectionHistory } from "./pages/history/broker-h/broker-h.component";
import { OrganizationsHistory } from "./pages/history/org-h/org-h.component";
import { UsersHistory } from "./pages/history/users-h/users-h.component";
import { AccountsHistory } from "./pages/history/accounts-h/accounts-h.component";
import { AccountGroupsHistory } from "./pages/history/account-groups-h/account-groups-h.component";
import { FXBrokersHistory } from "./pages/history/fx-brokers-h/fx-brokers-h.component";
import { CurrencyGroupsHistory } from "./pages/history/currency-groups-h/currency-groups-h.component";
import { SystemIntegrationHistory } from "./pages/history/system-int-h/system-int-h.component";

import { MyProfileBranch } from "./pages/settings/myprofile/myprofile.component";
import { UsersBranch } from "./pages/settings/users/users.component";
import { OrganizationsBranch } from "./pages/settings/organizations/organizations.component";
import { AccountBranch } from "./pages/settings/account/account.component";
import { AccountGroupsBranch } from "./pages/settings/account-groups/account-groups.component";
import { FXBrokersBranch } from "./pages/settings/fx-brokers/fx-brokers.component";
import { CurrencyGroupsBranch } from "./pages/settings/currency-groups/currency-groups.component";
import { SystemIntegrationBranch } from "./pages/settings/system-integration/system-integration.component";

import { LoginPage } from "./pages/login/login.component";
import { TargetsBranch } from "./pages/rules/targets/targets.component";
import { MinimumOrderQuantityBranch } from "./pages/settings/minimum-order-quantity/minimum-order-quantity.component";
import { OrganizationHolidayBranch } from "./pages/settings/holidays/organization.holidays.component";
import { SecurityQuantityRatioBranch } from "./pages/settings/security-quantity-ratio/security-quantity-ratio.component";
import { NotificationChangePassword } from "./pages/notification/change-password/change-password.component";
import { ForgotPasswordPage } from "./pages/forgot-password/forgot-password.component";

const routes: Routes = [
  {
    path: "",
    component: SiteComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: "404",
        component: NotFoundComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "",
        redirectTo: "exposure",
        pathMatch: "full",
      },
      {
        path: "organization",
        component: OrganizationsBranch,
        canActivate: [AuthGuard],
        data: {
          title: "Organization",
          branch: "",
          permissions: {
            primary: "clientOrganization",
            secondary: [],
          },
        },
      },
      {
        path: "exposure",
        children: [
          {
            path: "",
            redirectTo: "hedge-status",
            pathMatch: "full",
          },
          {
            path: "hedge-status",
            component: HedgeBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Hedge Status",
              branch: "Currency and Funding",
              permissions: {
                primary: ["clientExposure"],
                secondary: ["hedgeAssetTrades"],
              },
            },
          },
          {
            path: "fund-status",
            component: FundBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Fund Status",
              branch: "Currency and Funding",
              permissions: {
                primary: ["clientExposure"],
                secondary: ["fundAssetTrades"],
              },
            },
          },
          {
            path: "acc-exp-hedge-status",
            component: AccExpHedgeBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Account Hedge Status",
              branch: "Currency and Funding",
              permissions: {
                primary: ["clientExposure"],
                secondary: ["hedgeAssetTrades"],
              },
            },
          },
          {
            path: "acc-exp-fund-status",
            component: AccExpFundBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Account Fund Status",
              branch: "Currency and Funding",
              permissions: {
                primary: ["clientExposure"],
                secondary: ["fundAssetTrades"],
              },
            },
          },
        ],
      },
      {
        path: "rules",
        children: [
          {
            path: "",
            redirectTo: "timing",
            pathMatch: "full",
          },
          {
            path: "timing",
            component: TimingBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Timing",
              branch: "default",
              permissions: {
                primary: ["clientRule"],
                secondary: [],
              },
            },
          },
          {
            path: "targets",
            component: TargetsBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Targets",
              branch: "default",
              permissions: {
                primary: ["clientRule"],
                secondary: [],
              },
            },
          },
          {
            path: "netting",
            component: NettingBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Netting",
              branch: "default",
              permissions: {
                primary: ["clientRule"],
                secondary: [],
              },
            },
          },
          {
            path: "broker-selection",
            component: BrokerSelectionBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Broker Selection",
              branch: "default",
              permissions: {
                primary: ["clientRule"],
                secondary: [],
              },
            },
          },
        ],
      },
      {
        path: "orders",
        children: [
          {
            path: "",
            redirectTo: "openfx",
            pathMatch: "full",
          },
          {
            path: "openfx",
            component: OpenFXBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Open FX Orders",
              branch: "default",
              permissions: {
                primary: ["clientRebalance"],
                secondary: [],
              },
            },
          },
          {
            path: "hedge-trades",
            component: HedgeTradesBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Hedge Broker Positions",
              branch: "default",
              permissions: {
                primary: ["clientRebalance"],
                secondary: ["hedgeAssetTrades"],
              },
            },
          },
        ],
      },
      {
        path: "history",
        children: [
          {
            path: "",
            redirectTo: "fx-orders",
            pathMatch: "full",
          },
          {
            path: "fx-orders",
            component: FXOrdersHistory,
            canActivate: [AuthGuard],
            data: {
              title: "FX Orders",
              branch: "Summary",
              permissions: {
                primary: ["clientExposure"],
                secondary: ["hedgeAssetTrades"],
              },
            },
          },
          // {
          //   path: 'sod-cash',
          //   component: SODCashHistory,
          //   canActivate: [AuthGuard],
          //   data: {
          //     title: 'Start of Day Cash',
          //     branch: 'Positions Event Log',
          //   }
          // },
          // {
          //   path: 'sod-forwards',
          //   component: SODForwardsHistory,
          //   canActivate: [AuthGuard],
          //   data: {
          //     title: 'Start of Day Forwards',
          //     branch: 'Positions Event Log',
          //   }
          // },
          // {
          //   path: 'sod-assets',
          //   component: SODAssetsHistory,
          //   canActivate: [AuthGuard],
          //   data: {
          //     title: 'Start of Day Assets',
          //     branch: 'Positions Event Log',
          //   }
          // },
          {
            path: "asset-trades",
            component: AssetTradesHistory,
            canActivate: [AuthGuard],
            data: {
              title: "Asset Trades",
              branch: "Trading Event Log",
              permissions: {
                primary: ["clientExposure"],
                secondary: [],
              },
            },
          },
          {
            path: "submitted-fx-orders",
            component: SubFXOrdersHistory,
            canActivate: [AuthGuard],
            data: {
              title: "Submitted FX Orders",
              branch: "Trading Event Log",
              permissions: {
                primary: ["clientExposure"],
                secondary: [],
              },
            },
          },
          {
            path: "fx-order-recommendations",
            component: FXOrderRecommendationsHistory,
            canActivate: [AuthGuard],
            data: {
              title: "FX Order Recommendations",
              branch: "Trading Event Log",
              permissions: {
                primary: ["clientExposure"],
                secondary: [],
              },
            },
          },
          {
            path: "internal-allocations",
            component: InternalAllocationsHistory,
            canActivate: [AuthGuard],
            data: {
              title: "Internal Allocations",
              branch: "Trading Event Log",
              permissions: {
                primary: ["clientExposure"],
                secondary: [],
              },
            },
          },
          {
            path: "timing",
            component: TimingHistory,
            canActivate: [AuthGuard],
            data: {
              title: "Timing",
              branch: "Rule Event Log",
              permissions: {
                primary: ["clientRule"],
                secondary: [],
              },
            },
          },
          {
            path: "netting",
            component: NettingHistory,
            canActivate: [AuthGuard],
            data: {
              title: "Targets",
              branch: "Rule Event Log",
              permissions: {
                primary: ["clientRule"],
                secondary: [],
              },
            },
          },
          {
            path: "broker-selection",
            component: BrokerSelectionHistory,
            canActivate: [AuthGuard],
            data: {
              title: "Broker Selection",
              branch: "Rule Event Log",
              permissions: {
                primary: ["clientRule"],
                secondary: [],
              },
            },
          },
          {
            path: "organizations",
            component: OrganizationsHistory,
            canActivate: [AuthGuard],
            data: {
              title: "Organization",
              branch: "Organization Event Log",
              permissions: {
                primary: ["clientOrganization"],
                secondary: [],
              },
            },
          },
          {
            path: "users",
            component: UsersHistory,
            canActivate: [AuthGuard],
            data: {
              title: "Users",
              branch: "Organization Event Log",
              permissions: {
                primary: ["clientOrganization"],
                secondary: [],
              },
            },
          },
          {
            path: "accounts",
            component: AccountsHistory,
            canActivate: [AuthGuard],
            data: {
              title: "Accounts",
              branch: "Reference Data Event Log",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "account-groups",
            component: AccountGroupsHistory,
            canActivate: [AuthGuard],
            data: {
              title: "Account Groups",
              branch: "Reference Data Event Log",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "fx-brokers",
            component: FXBrokersHistory,
            canActivate: [AuthGuard],
            data: {
              title: "FX Brokers",
              branch: "Reference Data Event Log",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "currency-groups",
            component: CurrencyGroupsHistory,
            canActivate: [AuthGuard],
            data: {
              title: "Currency Groups",
              branch: "Reference Data Event Log",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "system-integration",
            component: SystemIntegrationHistory,
            canActivate: [AuthGuard],
            data: {
              title: "System Integration",
              branch: "Integration Event Log",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
        ],
      },
      {
        path: "notifications",
        children: [
          {
            path: "",
            redirectTo: "change-password",
            pathMatch: "full",
          },
          {
            path: "change-password",
            component: NotificationChangePassword,
            canActivate: [AuthGuard],
            data: {
              title: "Change password",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
        ],
      },
      {
        path: "settings",
        children: [
          {
            path: "",
            redirectTo: "my-account",
            pathMatch: "full",
          },
          {
            path: "my-account",
            component: MyProfileBranch,
            canActivate: [AuthGuard],
            data: {
              title: "My Profile",
              branch: "default",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "organization",
            component: OrganizationsBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Organization",
              branch: "Organization",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "users",
            component: UsersBranch,
            canActivate: [AuthGuard],
            data: {
              title: "Users",
              branch: "Organization",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "accounts",
            component: AccountBranch,
            outlet: "primary",
            canActivate: [AuthGuard],
            data: {
              title: "Accounts",
              branch: "Reference Data",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "account-groups",
            component: AccountGroupsBranch,
            outlet: "primary",
            canActivate: [AuthGuard],
            data: {
              title: "Account Groups",
              branch: "Reference Data",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "fx-brokers",
            component: FXBrokersBranch,
            outlet: "primary",
            canActivate: [AuthGuard],
            data: {
              title: "FX Brokers",
              branch: "Reference Data",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },

          {
            path: "organization-holidays",
            component: OrganizationHolidayBranch,
            outlet: "primary",
            canActivate: [AuthGuard],
            data: {
              title: "Holidays",
              branch: "Reference Data",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "minimum-order-quantity",
            component: MinimumOrderQuantityBranch,
            outlet: "primary",
            canActivate: [AuthGuard],
            data: {
              title: "Minimum Order Quantities",
              branch: "Reference Data",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "security-quantity-ratio",
            component: SecurityQuantityRatioBranch,
            outlet: "primary",
            canActivate: [AuthGuard],
            data: {
              title: "Security Quantity Ratios",
              branch: "Reference Data",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "currency-groups",
            component: CurrencyGroupsBranch,
            outlet: "primary",
            canActivate: [AuthGuard],
            data: {
              title: "Currency Groups",
              branch: "Reference Data",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
          {
            path: "system-integration",
            component: SystemIntegrationBranch,
            outlet: "primary",
            canActivate: [AuthGuard],
            data: {
              title: "System Integration",
              branch: "System",
              permissions: {
                primary: [],
                secondary: [],
              },
            },
          },
        ],
      },
    ],
  },
  {
    path: "forgot-password",
    component: ForgotPasswordPage,
  },
  {
    path: "login",
    component: LoginPage,
    outlet: "primary",
  },
  { path: "**", redirectTo: "404" },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}



// WEBPACK FOOTER //
// /app/wiserisk-core/src/main/ng4app/src/app/app-routing.module.ts